/**
 * @file This file contains the configuration for all game achievements.
 * @module achievementsConfig
 */

/**
 * @typedef {Object} Achievement
 * @property {string} key - The unique identifier for the achievement.
 * @property {string} name - The display name of the achievement.
 * @property {string} description - The description shown to the player.
 * @property {string} icon - An emoji or icon for the achievement.
 * @property {object} trigger - The condition that unlocks the achievement.
 * @property {'ITEM_CRAFTED'} trigger.type - The type of event that triggers the check.
 * @property {string} trigger.itemName - The name of the item that must be crafted.
 * @property {number} [trigger.targetCount=1] - The number of times the event must occur.
 */


export const achievementsConfig = {
    'MADE_FIRST_SEEDLING': {
        key: 'MADE_FIRST_SEEDLING',
        name: 'First Growth',
        description: 'Successfully merge your first Sprout in a campaign game.',
        icon: '🌱',
        scope: 'lifetime',
        trigger: {
            type: 'ITEM_CRAFTED',
            itemName: 'Sprout',
            targetCount: 1
        }
    },
    'MADE_TEN_SPROUTS': {
        key: 'MADE_TEN_SPROUTS',
        name: 'Green Thumb',
        description: 'Create 10 Sprouts in campaign games.',
        icon: '10🌱',
        scope: 'lifetime',
        trigger: {
            type: 'ITEM_CRAFTED',
            itemName: 'Sprout',
            targetCount: 10
        }
    },
    'DOUBLE_MERGE': {
        key: 'DOUBLE_MERGE',
        name: 'Chain Reaction',
        description: 'Perform a double merge in a single turn during a campaign game.',
        icon: '⛓️',
        scope: 'lifetime',
        trigger: {
            type: 'CONSECUTIVE_MERGES',
            targetCount: 2
        },
        showEveryTime: true
    },
    'TRIPLE_MERGE': {
        key: 'TRIPLE_MERGE',
        name: 'Master of Chaos',
        description: 'Perform a triple merge in a single turn during a campaign game.',
        icon: '💥',
        scope: 'lifetime',
        trigger: {
            type: 'CONSECUTIVE_MERGES',
            targetCount: 3
        },
        showEveryTime: true
    },
    'SESSION_MERGES_20': {
        key: 'SESSION_MERGES_20',
        name: 'Merge Apprentice',
        description: 'Perform 20 merges in a single campaign game.',
        icon: '🛠️',
        scope: 'session',
        trigger: {
            type: 'TOTAL_MERGES_SESSION',
            targetCount: 20
        }
    },
    'LIFETIME_MERGES_100': {
        key: 'LIFETIME_MERGES_100',
        name: 'Merge Artisan',
        description: 'Perform 100 total merges across all campaign games.',
        icon: '🏆',
        scope: 'lifetime',
        trigger: {
            type: 'TOTAL_MERGES_LIFETIME',
            targetCount: 100
        }
    },

    // --- Score & Gold Achievements ---
    'FIRST_GOLD': {
        key: 'FIRST_GOLD',
        name: 'First Gold',
        description: 'Earn your first piece of gold from a milestone merge.',
        icon: '🥇',
        scope: 'lifetime',
        trigger: {
            type: 'GOLD_LIFETIME',
            targetCount: 1
        }
    },
    'SCORE_10K_SESSION': {
        key: 'SCORE_10K_SESSION',
        name: 'High Scorer',
        description: 'Reach 10,000 points in a single campaign game.',
        icon: '🎯',
        scope: 'session',
        trigger: {
            type: 'SCORE_SESSION',
            targetCount: 10000
        }
    },
    'GOLD_100_LIFETIME': {
        key: 'GOLD_100_LIFETIME',
        name: 'Gold Hoarder',
        description: 'Accumulate 100 gold across all games.',
        icon: '💰',
        scope: 'lifetime',
        trigger: {
            type: 'GOLD_LIFETIME',
            targetCount: 100
        }
    },

    // --- Completionist Achievements ---
    'FIRST_LEVEL_COMPLETE': {
        key: 'FIRST_LEVEL_COMPLETE',
        name: 'First Victory',
        description: 'Complete your first campaign level.',
        icon: '🎉',
        scope: 'lifetime',
        trigger: {
            type: 'WIN_CONDITION',
            levelIndex: 0,
            targetCount: 1
        }
    },
    'COMPLETE_PATH_HUGE': {
        key: 'COMPLETE_PATH_HUGE',
        name: 'Epic Saga',
        description: 'Craft the final item on a "Huge" length upgrade path.',
        icon: '📜',
        scope: 'lifetime',
        trigger: {
            type: 'WIN_CONDITION',
            pathLength: 'huge'
        }
    },
    'WIN_ON_TINY_GRID': {
        key: 'WIN_ON_TINY_GRID',
        name: 'Tiny Titan',
        description: 'Win a campaign level on a "Tiny" (5x5) grid.',
        icon: '🔬',
        scope: 'lifetime',
        trigger: {
            type: 'WIN_CONDITION',
            gridSize: 5
        }
    },
    'WIN_CRAFTING_MODE': {
        key: 'WIN_CRAFTING_MODE',
        name: 'Master Crafter',
        description: 'Win a game in Crafting Mode.',
        icon: '🛠️',
        scope: 'lifetime',
        trigger: {
            type: 'WIN_CONDITION',
            gameMode: 'crafting'
        }
    },

    // --- Animal & Crate Achievements ---
    'FIRST_CRATE': {
        key: 'FIRST_CRATE',
        name: 'Animal Control',
        description: 'Successfully crate your first animal.',
        icon: '📦',
        scope: 'lifetime',
        trigger: {
            type: 'ANIMAL_CRATED',
            targetCount: 1
        }
    },
    'BUILT_A_ZOO': {
        key: 'BUILT_A_ZOO',
        name: 'Zookeeper',
        description: 'Build your first Zoo by merging crates.',
        icon: '🏞️',
        scope: 'lifetime',
        trigger: {
            type: 'ITEM_CRAFTED',
            itemName: 'Zoo',
            targetCount: 1
        }
    },

    // --- Turn-based Achievements ---
    'SURVIVE_100_TURNS': {
        key: 'SURVIVE_100_TURNS',
        name: 'Survivor',
        description: 'Survive for 100 turns in a single game.',
        icon: '⏳',
        scope: 'session',
        trigger: {
            type: 'TURN_COUNT_SESSION',
            targetCount: 100
        }
    },

    // --- Easter Egg & Oddity Achievements ---
    'PACIFIST_WIN': {
        key: 'PACIFIST_WIN',
        name: 'Pacifist',
        description: 'Win a campaign level without crating a single animal.',
        icon: '🕊️',
        scope: 'lifetime',
        trigger: {
            type: 'WIN_CONDITION',
            cratesBuilt: 0
        }
    },
    'ZERO_SCORE_LOSS': {
        key: 'ZERO_SCORE_LOSS',
        name: 'A for Effort?',
        description: 'Lose a game by filling the board without making a single merge.',
        icon: '🤷',
        scope: 'lifetime',
        trigger: {
            type: 'LOSE_CONDITION',
            score: 0
        }
    },

    // --- Crate Achievements (Session) ---
    'CRATE_APPRENTICE': {
        key: 'CRATE_APPRENTICE',
        name: 'Crate Apprentice',
        description: 'Crate 10 animals in a single campaign game.',
        icon: '📦x10',
        scope: 'session',
        trigger: {
            type: 'ANIMAL_CRATED_SESSION',
            targetCount: 10
        }
    },
    'CRATE_JOURNEYMAN': {
        key: 'CRATE_JOURNEYMAN',
        name: 'Crate Journeyman',
        description: 'Crate 20 animals in a single campaign game.',
        icon: '📦x20',
        scope: 'session',
        trigger: {
            type: 'ANIMAL_CRATED_SESSION',
            targetCount: 20
        }
    },
    'CRATE_MASTER': {
        key: 'CRATE_MASTER',
        name: 'Crate Master',
        description: 'Crate 30 animals in a single campaign game.',
        icon: '📦x30',
        scope: 'session',
        trigger: {
            type: 'ANIMAL_CRATED_SESSION',
            targetCount: 30
        }
    },

    // --- Special Item Achievements (Lifetime) ---
    'SUNRISE_SANCTUARY_1': {
        key: 'SUNRISE_SANCTUARY_1',
        name: 'Sanctuary Founder',
        description: 'Build your first Sunrise Sanctuary.',
        icon: '🌄',
        scope: 'lifetime',
        trigger: {
            type: 'ITEM_CRAFTED',
            itemName: 'Sunrise Sanctuary',
            targetCount: 1
        }
    },
    'SUNRISE_SANCTUARY_10': {
        key: 'SUNRISE_SANCTUARY_10',
        name: 'Sanctuary Architect',
        description: 'Build 10 Sunrise Sanctuaries across all games.',
        icon: '🌄x10',
        scope: 'lifetime',
        trigger: {
            type: 'ITEM_CRAFTED',
            itemName: 'Sunrise Sanctuary',
            targetCount: 10
        }
    },

    // --- Upgrade Path Milestones (Lifetime) ---
    'REACH_TIER_5': {
        key: 'REACH_TIER_5',
        name: 'Getting Started',
        description: 'Craft a Tier 5 item for the first time.',
        icon: '5️⃣',
        scope: 'lifetime',
        trigger: {
            type: 'REACH_TIER',
            targetCount: 5
        }
    },
    'REACH_TIER_10': {
        key: 'REACH_TIER_10',
        name: 'Adept Builder',
        description: 'Craft a Tier 10 item for the first time.',
        icon: '🔟',
        scope: 'lifetime',
        trigger: {
            type: 'REACH_TIER',
            targetCount: 10
        }
    },
    'REACH_TIER_15': {
        key: 'REACH_TIER_15',
        name: 'Master Architect',
        description: 'Craft a Tier 15 item for the first time.',
        icon: '1️⃣5️⃣',
        scope: 'lifetime',
        trigger: {
            type: 'REACH_TIER',
            targetCount: 15
        }
    },
    'REACH_TIER_20': {
        key: 'REACH_TIER_20',
        name: 'Legendary Creator',
        description: 'Craft a Tier 20 item for the first time.',
        icon: '2️⃣0️⃣',
        scope: 'lifetime',
        trigger: {
            type: 'REACH_TIER',
            targetCount: 20
        }
    },

    // --- Named Item Achievements (Lifetime) ---
    'CRAFT_WOODLAND_HALL': {
        key: 'CRAFT_WOODLAND_HALL',
        name: 'Lord of the Manor',
        description: 'Construct the magnificent Woodland Hall.',
        icon: '🏛️',
        scope: 'lifetime',
        trigger: {
            type: 'ITEM_CRAFTED',
            itemName: 'Woodland Hall',
            targetCount: 1
        }
    },
    'CRAFT_CITY_SKYLINE': {
        key: 'CRAFT_CITY_SKYLINE',
        name: 'Metropolis Planner',
        description: 'Build the iconic City Skyline.',
        icon: '🏙️',
        scope: 'lifetime',
        trigger: {
            type: 'ITEM_CRAFTED',
            itemName: 'City Skyline',
            targetCount: 1
        }
    },
    'CRAFT_FORTRESS_TEMPLE': {
        key: 'CRAFT_FORTRESS_TEMPLE',
        name: 'Temple Raider',
        description: 'Uncover the secrets of the Fortress Temple.',
        icon: '🏰',
        scope: 'lifetime',
        trigger: {
            type: 'ITEM_CRAFTED',
            itemName: 'Fortress Temple',
            targetCount: 1
        }
    },
    'CRAFT_GALAXY': {
        key: 'CRAFT_GALAXY',
        name: 'Astronomer',
        description: 'Chart a course through the Galaxy.',
        icon: '🌌',
        scope: 'lifetime',
        trigger: {
            type: 'ITEM_CRAFTED',
            itemName: 'Galaxy',
            targetCount: 1
        }
    },

    // --- Biome Achievements (Lifetime) ---
    'PLAYED_ALL_BIOMES': {
        key: 'PLAYED_ALL_BIOMES',
        name: 'World Traveler',
        description: 'Play at least one game in every available biome.',
        icon: '🗺️',
        scope: 'lifetime',
        trigger: {
            type: 'PLAYED_ALL_BIOMES'
        }
    },
    'WON_IN_WOODLAND_5': {
        key: 'WON_IN_WOODLAND_5',
        name: 'Forest Guardian',
        description: 'Win 5 games in the Woodland biome.',
        icon: '🌳x5',
        scope: 'lifetime',
        trigger: {
            type: 'WON_IN_BIOME',
            biomeKey: 'default',
            targetCount: 5
        }
    },
    'WON_IN_ALL_BIOMES': {
        key: 'WON_IN_ALL_BIOMES',
        name: 'Biome Master',
        description: 'Achieve victory in every available biome.',
        icon: '🌐',
        scope: 'lifetime',
        trigger: {
            type: 'WON_IN_ALL_BIomes'
        }
    },

    // --- Power-up Achievements (Lifetime) ---
    'FIRST_CLEANUP': {
        key: 'FIRST_CLEANUP',
        name: 'Demolitionist',
        description: 'Use the Clean up crew power-up for the first time.',
        icon: '👷🏼',
        scope: 'lifetime',
        trigger: {
            type: 'POWERUP_USED',
            powerupKey: 'powerup_cleanup',
            targetCount: 1
        }
    },
    'PERFECT_CLEANUP': {
        key: 'PERFECT_CLEANUP',
        name: 'Perfect Placement',
        description: 'Clear a full 3x3 area with a single Clean up crew.',
        icon: '👷🏼✨',
        scope: 'lifetime',
        trigger: {
            type: 'PERFECT_POWERUP',
            powerupKey: 'powerup_cleanup',
            cellsCleared: 9
        }
    },
    'NATURAL_TALENT': {
        key: 'NATURAL_TALENT',
        name: 'Natural Talent',
        description: 'Win a campaign level without using any power-ups.',
        icon: '💪',
        scope: 'lifetime',
        trigger: {
            type: 'WIN_CONDITION',
            powerupsUsed: 0
        }
    },

    // --- Disaster Achievements ---
    'FIRST_DISASTER': {
        key: 'FIRST_DISASTER',
        name: 'I Survived!',
        description: 'Survive your first disaster.',
        icon: '🛡️',
        scope: 'lifetime',
        trigger: {
            type: 'DISASTER_SURVIVED_LIFETIME',
            targetCount: 1
        }
    },
    'SURVIVE_5_DISASTERS': {
        key: 'SURVIVE_5_DISASTERS',
        name: 'Disaster Tourist',
        description: 'Survive 5 disasters in a single game.',
        icon: '🌪️x5',
        scope: 'session',
        trigger: {
            type: 'DISASTER_SURVIVED_SESSION',
            targetCount: 5
        }
    },
    'SURVIVE_10_DISASTERS': {
        key: 'SURVIVE_10_DISASTERS',
        name: 'Calamity Jane',
        description: 'Survive 10 disasters in a single game.',
        icon: '🦖x10',
        scope: 'session',
        trigger: {
            type: 'DISASTER_SURVIVED_SESSION',
            targetCount: 10
        }
    },
    'SURVIVE_100_DISASTERS_LIFETIME': {
        key: 'SURVIVE_100_DISASTERS_LIFETIME',
        name: 'Unflappable',
        description: 'Survive 100 total disasters.',
        icon: '💯',
        scope: 'lifetime',
        trigger: {
            type: 'DISASTER_SURVIVED_LIFETIME',
            targetCount: 100
        }
    },
    'LUCKY_WIN': {
        key: 'LUCKY_WIN',
        name: 'A Quiet Day',
        description: 'Win a campaign level without any disasters occurring.',
        icon: '😇',
        scope: 'lifetime',
        trigger: {
            type: 'WIN_CONDITION',
            disastersExperienced: 0
        }
    }
};